import { client } from 'src/enviroment/api/axios'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Query, Mutation } from '../index'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { ProjectsQueryParams } from 'src/types/projects'
import { ProjectType, TypeSearchParams } from '../../types/type'
import { queryTool } from '../../enviroment/lib/utils'
import { DEFAULT_TAKE } from '../../enviroment/constants/constants'

export const typeRouterKeys = {
  types: 'type.types',
  myProjectTypes: 'type.myProjectTypes',
  projectTypes: 'type.projectTypes',
} as const

export const typeRouter = {
  search: {
    useQuery: (params: TypeSearchParams = {}, opts?: Query<ProjectType[]>) => {
      const { query = '', applicableOnly } = params

      return useQuery<ProjectType[]>({
        queryKey: [typeRouterKeys.types, query],
        queryFn: async () => {
          return (
            await client.get<ProjectType[]>('/types', {
              params: {
                query: queryTool(query),
                applicableOnly,
              },
            })
          ).data
        },
        ...opts,
      })
    },
  },
  //NOTE: DEPRECATED, using projectTypes instead
  myProjectTypes: {
    useQuery: (params: ProjectsQueryParams, opts?: Query<ProjectType[]>) => {
      const tenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<ProjectType[]>({
        queryKey: [typeRouterKeys.myProjectTypes, tenantId, params],
        queryFn: async () => {
          return (
            await client.get<ProjectType[]>(`/tenant/${tenantId}/types:my-projects`, {
              params,
            })
          ).data
        },
        enabled: Boolean(tenantId),
        ...opts,
      })
    },
  },
  create: {
    useMutation: (opts?: Mutation<string, ProjectType>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (name: string) => {
          return (await client.post<ProjectType>('/types', { name })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: [typeRouterKeys.types],
          })
          queryClient.invalidateQueries({
            queryKey: [typeRouterKeys.projectTypes],
          })
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  delete: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (typeId: string) => {
          await client.delete(`/types/${typeId}`)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: [typeRouterKeys.types],
          })
          queryClient.invalidateQueries({
            queryKey: [typeRouterKeys.projectTypes],
          })
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  projectTypes: {
    useInfiniteQuery: () => {
      const tenantId = useTenantStore((state) => state.currentTenant?.id)

      return useInfiniteQuery<ProjectType[]>({
        queryKey: [typeRouterKeys.projectTypes, tenantId],
        queryFn: async ({ pageParam: cursorId }) => {
          return (
            await client.get<ProjectType[]>(`/tenants/${tenantId}/projects/types`, {
              params: { cursorId, take: 50 },
            })
          ).data
        },
        getNextPageParam: (lastPage) => lastPage.at(-1)?.id,
        enabled: Boolean(tenantId),
      })
    },
  },
}
